import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  GiftIcon,
  QuestionMarkCircleIcon,
  UsersIcon,
  StarIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import AuthModal from '../components/AuthModal';
import CartPopup from '../components/CartPopup';
import ProductCard from '../components/ui/ProductCard';
import { AKITO_CONNECT_VENDOR } from '../constants/akitoConnectVendor';
import { productService, Product } from '../services/productService';
import {
  HomePageTabId,
  PRODUCT_CATEGORIES,
  getCategoryDisplayName,
} from '../constants/productCategories';
import {
  getProductLineShippingCost,
  isDigitalProduct,
} from '../utils/productShipping';
import StripeService from '../services/stripeService';
import SupportTicketModal from '../components/SupportTicketModal';
import { useSearch, SearchCategory } from '../contexts/SearchContext';
import { scrollToSection } from '../utils/siteNavigation';
import {
  filterProductsByCategory,
  filterProductsByQuery,
} from '../utils/productSearch';
import '../App.css';

// Transform Supabase product to match the existing product card format
const resolveImageUrl = (url?: string) => {
  if (!url) return url;
  if (url.startsWith('http') || url.startsWith('//')) return url;
  return `${process.env.PUBLIC_URL ?? ''}${url}`;
};

const getVendorDisplayName = (vendorId: string) => {
  if (vendorId === AKITO_CONNECT_VENDOR.userId) return AKITO_CONNECT_VENDOR.businessName;
  return undefined;
};

type ProductImage = Product['images'][string];

const transformProduct = (product: Product) => {
  // Get primary image or first available image
  const images = Object.values(product.images || {}) as ProductImage[];
  const primaryImage = images.find(img => img.isPrimary) || images[0];
  const vendorName = getVendorDisplayName(product.vendorId);
  
  // Calculate discount percentage
  const discountPercentage = product.price.discountPercentage 
    ? `-${product.price.discountPercentage}%`
    : product.price.original > product.price.current 
      ? `-${Math.round(((product.price.original - product.price.current) / product.price.original) * 100)}%`
      : '';

  // Format price with currency
  const formatPrice = (price: number) => `£${price.toFixed(2)}`;

  // Determine category display name
  return {
    id: product.productId,
    name: product.name,
    category: getCategoryDisplayName(product.categoryId),
    vendorName,
    currentPrice: formatPrice(product.price.current),
    originalPrice: product.price.original > product.price.current ? formatPrice(product.price.original) : '',
    discount: discountPercentage,
    badge: product.isFeatured ? 'Featured' : product.isTrending ? 'Trending' : 'New',
    rating: product.metrics.averageRating || 0,
    reviewCount: product.metrics.reviewCount || 0,
    image: resolveImageUrl(primaryImage?.url) || `https://via.placeholder.com/150x150/FFB366/FFFFFF?text=${encodeURIComponent(product.name)}`,
    vendorId: product.vendorId,
    categoryId: product.categoryId,
    sku: product.inventory?.sku || product.productId,
    shippingCost: getProductLineShippingCost(product),
    isDigital: isDigitalProduct(product),
  };
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<HomePageTabId>('all');
  const [isAnimating, setIsAnimating] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutMessage, setCheckoutMessage] = useState<string | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const { isAuthenticated, openAuthModal } = useAuth();
  const { isCartOpen, closeCart, addToCart, clearCart } = useCart();
  const {
    appliedQuery,
    applyCategory,
    clearAppliedSearch,
    restoreSearch,
  } = useSearch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    const orderId = params.get('order_id');
    const sessionId = params.get('session_id');

    const finishCheckout = async () => {
      if (checkoutStatus === 'success') {
        clearCart().catch((err) => console.warn('Failed to clear cart after checkout:', err));

        if (sessionId && isAuthenticated) {
          try {
            await StripeService.getInstance().confirmCheckoutSession(sessionId);
          } catch (err) {
            console.warn('Checkout confirmation fallback failed:', err);
          }
        }

        setCheckoutMessage(
          orderId
            ? `Payment successful! Your order #${orderId.slice(-8)} is confirmed.`
            : 'Payment successful! Your order is confirmed.'
        );
      } else if (checkoutStatus === 'cancelled') {
        setCheckoutMessage('Checkout was cancelled. Your cart is unchanged — you can try again when ready.');
      }

      if (checkoutStatus) {
        params.delete('checkout');
        params.delete('order_id');
        params.delete('session_id');
        const nextQuery = params.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
        window.history.replaceState({}, '', nextUrl);
      }
    };

    finishCheckout();
  }, [clearCart, isAuthenticated]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scrollTarget = params.get('scroll');
    const category = params.get('category') as HomePageTabId | null;
    const searchQuery = params.get('q');
    const openSupport = params.get('support') === '1';

    const validCategory =
      category && PRODUCT_CATEGORIES.some((item) => item.id === category)
        ? category
        : null;

    if (searchQuery || validCategory) {
      restoreSearch(
        searchQuery || '',
        (validCategory || 'all') as SearchCategory
      );
    }

    if (validCategory) {
      setActiveTab(validCategory);
      setIsAnimating(true);
    } else if (searchQuery) {
      setActiveTab('all');
      setIsAnimating(true);
    }

    if (scrollTarget) {
      window.setTimeout(() => scrollToSection(scrollTarget), 150);
    }

    if (openSupport) {
      window.setTimeout(() => {
        if (isAuthenticated) {
          setShowSupportModal(true);
        } else {
          openAuthModal('login');
        }
      }, 200);
    }

    if (scrollTarget || category || searchQuery || openSupport) {
      params.delete('scroll');
      params.delete('category');
      params.delete('q');
      params.delete('support');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [isAuthenticated, openAuthModal, restoreSearch]);

  useEffect(() => {
    const handleSearchSubmitted = (event: Event) => {
      const category = (event as CustomEvent<{ category?: SearchCategory }>).detail?.category || 'all';
      setIsAnimating(true);
      setActiveTab(category === 'all' ? 'all' : category);
    };

    const handleBrowseCategory = (event: Event) => {
      const categoryId = (event as CustomEvent<{ categoryId?: HomePageTabId }>).detail?.categoryId;
      if (categoryId && PRODUCT_CATEGORIES.some((item) => item.id === categoryId)) {
        clearAppliedSearch();
        applyCategory(categoryId);
        setIsAnimating(true);
        setActiveTab(categoryId);
        scrollToSection('shop-products');
      }
    };

    const handleOpenSupport = () => {
      if (!isAuthenticated) {
        openAuthModal('login');
        return;
      }
      setShowSupportModal(true);
    };

    window.addEventListener('worldasia:search-submitted', handleSearchSubmitted);
    window.addEventListener('worldasia:browse-category', handleBrowseCategory);
    window.addEventListener('worldasia:open-customer-support', handleOpenSupport);
    return () => {
      window.removeEventListener('worldasia:search-submitted', handleSearchSubmitted);
      window.removeEventListener('worldasia:browse-category', handleBrowseCategory);
      window.removeEventListener('worldasia:open-customer-support', handleOpenSupport);
    };
  }, [isAuthenticated, openAuthModal, applyCategory, clearAppliedSearch]);

  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const activeProducts = await productService.getAllActiveProducts(100);
        const transformedProducts = activeProducts.map(transformProduct);
        setProducts(transformedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again later.');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Handle animation timing for stagger effect
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  const handleTabClick = (category: HomePageTabId) => {
    if (category === activeTab && !appliedQuery) return;

    clearAppliedSearch();
    applyCategory(category);
    setIsAnimating(true);
    setActiveTab(category);
  };

  const goComingSoon = (feature: string) => {
    navigate(`/coming-soon?feature=${encodeURIComponent(feature)}`);
  };

  const browseCategory = (categoryId: HomePageTabId) => {
    clearAppliedSearch();
    applyCategory(categoryId);
    setIsAnimating(true);
    setActiveTab(categoryId);
    scrollToSection('shop-products');
  };

  const handleCustomerSupport = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }
    setShowSupportModal(true);
  };

  const handleTrackOrders = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }
    window.dispatchEvent(
      new CustomEvent('worldasia:open-account-settings', { detail: { tab: 'orders' } })
    );
  };

  const handleFavoriteClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
    } else {
      // TODO: Add to favorites
      console.log('Adding to favorites');
    }
  };

  const handleAddToCartClick = async (product: any) => {
    try {
      const price = parseFloat(product.currentPrice.replace('£', ''));

      const cartItem = {
        productId: product.id,
        vendorId: product.vendorId || 'default-vendor',
        name: product.name,
        image: product.image || `https://via.placeholder.com/150x150/FFB366/FFFFFF?text=${encodeURIComponent(product.name)}`,
        price: price,
        quantity: 1,
        shippingCost: product.shippingCost ?? 0,
        isDigital: product.isDigital ?? false,
        category: product.category,
        sku: product.sku || product.id,
      };

      await addToCart(cartItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleShopNowClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
    } else {
      // TODO: Navigate to shop
      console.log('Navigating to shop');
    }
  };

  const handleViewAllClick = (section: string) => {
    if (section === 'categories') {
      scrollToSection('categories');
      return;
    }
    scrollToSection('shop-products');
  };

  const handleCategoryCardClick = (category: HomePageTabId) => {
    browseCategory(category);
  };

  const filteredProducts = filterProductsByQuery(
    filterProductsByCategory(products, activeTab),
    appliedQuery
  );

  const handleBecomeVendorClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
    } else {
      // Open vendor signup modal
      openAuthModal('vendor-signup');
    }
  };

  return (
    <div className="App">
      <Navigation />
      {checkoutMessage && (
        <div
          style={{
            maxWidth: 960,
            margin: '16px auto 0',
            padding: '12px 16px',
            borderRadius: 8,
            background: checkoutMessage.includes('cancelled') ? '#fff7ed' : '#ecfdf5',
            border: `1px solid ${checkoutMessage.includes('cancelled') ? '#fed7aa' : '#a7f3d0'}`,
            color: checkoutMessage.includes('cancelled') ? '#9a3412' : '#065f46',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{checkoutMessage}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!checkoutMessage.includes('cancelled') && (
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent('worldasia:open-account-settings', { detail: { tab: 'orders' } })
                  );
                  setCheckoutMessage(null);
                }}
                style={{
                  background: 'white',
                  border: '1px solid #a7f3d0',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#065f46',
                }}
              >
                View orders
              </button>
            )}
            <button
              type="button"
              onClick={() => setCheckoutMessage(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-text">
              <h2 className="hero-subtitle">Your Gateway to Asian Shopping</h2>
              <h1 className="hero-title">
                Discover <span className="highlight">Asian products</span> and services
              </h1>
              <p className="hero-description">
                Connect with authentic Asian vendors for clothing, electronics, food, and services. Experience the convenience of a multicultural marketplace right at your fingertips.
              </p>
              <button className="hero-button" onClick={handleShopNowClick}>Shop Now →</button>
            </div>
          </div>
        </section>
        
        {/* Shop by Categories Section */}
        <section className="categories-section" id="categories">
          <div className="section-header">
            <h2 className="section-title">Browse by categories</h2>
            <a href="#all-categories" className="view-all-link" onClick={(e) => { e.preventDefault(); handleViewAllClick('categories'); }}>All Categories →</a>
          </div>
          <div className="categories-grid">
            {PRODUCT_CATEGORIES.map((category) => (
              <div
                key={category.id}
                className={`category-card ${category.id}`}
                onClick={() => handleCategoryCardClick(category.id)}
              >
                <div className="category-content">
                  <h3 className="category-name">{category.name}</h3>
                  <p className="category-subtitle">{category.subtitle}</p>
                </div>
                <div className="category-image">
                </div>
              </div>
            ))}
          </div>
        </section>
        
        {/* All Products Section */}
        <section className="deals-section" id="shop-products">
          <div className="section-header">
            <h2 className="section-title">Shop All Products</h2>
          </div>
          
          <div className="deals-tabs">
            <button
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => handleTabClick('all')}
            >
              All
            </button>
            {PRODUCT_CATEGORIES.map((category) => (
              <button
                key={category.id}
                className={`tab-button ${activeTab === category.id ? 'active' : ''}`}
                onClick={() => handleTabClick(category.id)}
              >
                {category.tabLabel}
              </button>
            ))}
          </div>
          
          <div className={`deals-grid ${isAnimating ? 'tab-changing' : ''}`} key={activeTab} data-key={activeTab}>
            {loading ? (
              <div className="loading-message">Loading products...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-search-message">
                {appliedQuery
                  ? `No products found for "${appliedQuery}"${activeTab !== 'all' ? ` in ${PRODUCT_CATEGORIES.find((item) => item.id === activeTab)?.name || 'this category'}` : ''}.`
                  : 'No products found in this category yet.'}
              </div>
            ) : (
              filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onFavorite={handleFavoriteClick}
                  onAddToCart={handleAddToCartClick}
                />
              ))
            )}
          </div>
        </section>
        
        {/* Vendor Subscription Banner Section */}
        <section className="subscription-banner" id="become-vendor">
          <div className="subscription-content">
            <div className="subscription-text">
              <h2 className="subscription-subtitle">Become a Worldasia Vendor</h2>
              <h1 className="subscription-title">
                Start selling your <span className="highlight">Asian products</span> and services
              </h1>
              <p className="subscription-description">
                Join our marketplace as a vendor and reach customers worldwide. Choose from Basic or Pro subscription plans to boost your visibility and grow your business.
              </p>
              <button className="subscribe-button" onClick={handleBecomeVendorClick}>Become a Vendor →</button>
            </div>
          </div>
        </section>
        
        {/* Features Section */}
        <section className="features-section">
          <div className="features-header">
            <h2 className="features-title">
              Your Gateway to <span className="highlight">Asian Shopping</span> Nationwide
            </h2>
            <p className="features-description">
              Connect with authentic Asian vendors for products and services. Experience multicultural commerce with location-based discovery and secure payments.
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <MapPinIcon className="feature-svg-icon" />
              </div>
              <h3 className="feature-name">Location-Based Discovery</h3>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <ShieldCheckIcon className="feature-svg-icon" />
              </div>
              <h3 className="feature-name">Secure Payments</h3>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <UsersIcon className="feature-svg-icon" />
              </div>
              <h3 className="feature-name">Vendor Management</h3>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <StarIcon className="feature-svg-icon" />
              </div>
              <h3 className="feature-name">Customer Reviews</h3>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <QuestionMarkCircleIcon className="feature-svg-icon" />
              </div>
              <h3 className="feature-name">24/7 Support</h3>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          {/* Company Information & Payments */}
          <div className="footer-section company-info">
            <div className="company-logo">
              <img src="https://i.imghippo.com/files/YbW7011PQ.png" alt="Worldasia Logo" className="logo-image" />
            </div>
            <p className="company-tagline">
              Your gateway to Asian shopping. Connect with authentic vendors for products and services worldwide.
            </p>
            
            <div className="payment-section">
              <h4 className="payment-title">Accepted Payments</h4>
              <div className="payment-methods">
                <div className="payment-method">
                  <img src="https://i.imghippo.com/files/ef9010.png" alt="Apple Pay" className="payment-logo" />
                </div>
                <div className="payment-method">
                  <img src="https://i.imghippo.com/files/qBf8213sg.png" alt="Google Pay" className="payment-logo" />
                </div>
                <div className="payment-method">
                  <img src="https://i.imghippo.com/files/FGD5500DG.png" alt="PayPal" className="payment-logo" />
                </div>
                <div className="payment-method">
                  <img src="https://i.imghippo.com/files/xlVv6972Lnw.png" alt="Visa" className="payment-logo" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Department Links */}
          <div className="footer-section">
            <h4 className="footer-title">Categories</h4>
            <ul className="footer-links-list">
              {PRODUCT_CATEGORIES.map((category) => (
                <li key={category.id}>
                  <a
                    href={`#${category.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      browseCategory(category.id);
                    }}
                  >
                    {category.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* About Us Links */}
          <div className="footer-section">
            <h4 className="footer-title">About us</h4>
            <ul className="footer-links-list">
              <li><a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>About Worldasia</a></li>
              <li><a href="/coming-soon?feature=help" onClick={(e) => { e.preventDefault(); goComingSoon('help'); }}>Help</a></li>
              <li><a href="/coming-soon?feature=press" onClick={(e) => { e.preventDefault(); goComingSoon('press'); }}>Press Center</a></li>
            </ul>
          </div>
          
          {/* Services Links */}
          <div className="footer-section">
            <h4 className="footer-title">Services</h4>
            <ul className="footer-links-list">
              <li><a href="#become-vendor" onClick={(e) => { e.preventDefault(); handleBecomeVendorClick(); }}>Become a Vendor</a></li>
              <li><a href="/coming-soon?feature=mobile-app" onClick={(e) => { e.preventDefault(); goComingSoon('mobile-app'); }}>Mobile App</a></li>
              <li><a href="/coming-soon?feature=subscription" onClick={(e) => { e.preventDefault(); goComingSoon('subscription'); }}>Subscription Plans</a></li>
              <li><a href="#support" onClick={(e) => { e.preventDefault(); handleCustomerSupport(); }}>Customer Support</a></li>
            </ul>
          </div>
          
          {/* Help Links */}
          <div className="footer-section">
            <h4 className="footer-title">Help</h4>
            <ul className="footer-links-list">
              <li><a href="/coming-soon?feature=cart-help" onClick={(e) => { e.preventDefault(); goComingSoon('cart-help'); }}>Cart Help</a></li>
              <li><a href="#track-orders" onClick={(e) => { e.preventDefault(); handleTrackOrders(); }}>Track Orders</a></li>
              <li><a href="mailto:support@worldasia.co.uk">Contact us</a></li>
              <li><a href="#support" onClick={(e) => { e.preventDefault(); handleCustomerSupport(); }}>Help Center</a></li>
            </ul>
          </div>
          
          {/* Contact Information */}
          <div className="footer-section contact-info">
            <h4 className="footer-title">Contact</h4>
            <div className="contact-details">
              <div className="contact-item">
                <PhoneIcon className="contact-icon" width={12} height={12} />
                <a href="tel:+447939060549">07939 060549</a>
              </div>
              <div className="contact-item">
                <EnvelopeIcon className="contact-icon" width={12} height={12} />
                <a href="mailto:support@worldasia.co.uk">support@worldasia.co.uk</a>
              </div>
              <div className="contact-item">
                <MapPinIcon className="contact-icon" width={12} height={12} />
                <span>19 Newton Road, Sparkhill, Birmingham, B11 4PS</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-links">
            <a href="/coming-soon?feature=gift-cards" className="footer-link" onClick={(e) => { e.preventDefault(); goComingSoon('gift-cards'); }}>
              <GiftIcon className="link-icon" width={12} height={12} />
              Gift Cards
            </a>
            <a href="#support" className="footer-link" onClick={(e) => { e.preventDefault(); handleCustomerSupport(); }}>
              <QuestionMarkCircleIcon className="link-icon" width={12} height={12} />
              Help Center
            </a>
          </div>
          <div className="copyright">
            © All rights reserved by Worldasia 2024
          </div>
          <div className="legal-links">
            <a href="/coming-soon?feature=terms" onClick={(e) => { e.preventDefault(); goComingSoon('terms'); }}>Terms of service</a>
            <a href="/coming-soon?feature=privacy" onClick={(e) => { e.preventDefault(); goComingSoon('privacy'); }}>Privacy Policy</a>
          </div>
        </div>
      </footer>
      
      {/* Auth Modal */}
      <AuthModal />
      
      {/* Cart Popup */}
      <CartPopup isOpen={isCartOpen} onClose={closeCart} />

      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        type="customer_support"
      />
    </div>
  );
};

export default HomePage;
